import { format, parse } from 'date-fns'

/**
 * Format date strings with input format 'yyyy-MM-dd' with 'date-fns'
 */
export const formatDate = (date: string, outputFormat: string) => {
  const parsed = parse(date, 'yyyy-MM-dd', new Date())
  return format(parsed, outputFormat)
}

/**
 * This returns a "rough" representation of nighttime from 6pm to 7am
 */
export const isNightTime = (date: Date = new Date()) => {
  const timeStr = Number(format(date, 'Hmm'))
  return timeStr < 700 || timeStr >= 1800
}

/**
 * Format date strings with input format ISO 8601 date strings
 * (like 'YYYY-MM-DDT00:00:00.000Z') with 'Intl.DateTimeFormat'
 * to 'MM/DD/YYYY'
 * Important: This does adjust the provided date/time to the local timezone (assumes the provided date is in UTC)
 */
export const formatShortDateFromISODate = function (isoDateString: string) {
  try {
    const date = new Date(isoDateString)

    if (isNaN(date.getTime())) {
      throw new Error('Invalid date')
    }

    const finalDate = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(new Date(date))

    return finalDate
  } catch (e) {
    console.error(`Error parsing date time string: ${isoDateString}`, e)
    return ''
  }
}

/**
 * Date format helpers for various use-cases
 * https://yankovsky.github.io/intl.datetimeformat-cheatsheet
 */

/**
 * For legal, review and other pages
 */
export const dateLong = function (date: Date | string, timeZone: Intl.DateTimeFormatOptions['timeZone'] = 'UTC') {
  if (typeof date === 'string') date = new Date(date)
  return Intl.DateTimeFormat('default', { dateStyle: 'long', timeZone }).format(date)
}

/**
 * Format date as short readable format: "Dec 18, 2025"
 * Returns empty string for invalid/null dates
 */
export const dateShort = function (
  date: Date | string | null | undefined,
  timeZone: Intl.DateTimeFormatOptions['timeZone'] = 'UTC'
): string {
  if (!date) return ''
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return ''
    return Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone }).format(dateObj)
  } catch {
    return ''
  }
}

/**
 * Format date as a full timestamp: "Dec 18, 2025, 3:42:07 PM UTC"
 * For tooltips over a `dateShort` value, where the day alone is ambiguous.
 * Defaults to UTC to match `dateShort`, so the two never disagree on the day.
 * Returns empty string for invalid/null dates
 */
export const dateTimeFull = function (
  date: Date | string | null | undefined,
  timeZone: Intl.DateTimeFormatOptions['timeZone'] = 'UTC'
): string {
  if (!date) return ''
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return ''
    return Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      timeZone,
    }).format(dateObj)
  } catch {
    return ''
  }
}
